import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  Button,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../theme";
import { GET } from "../Functions/apiFunction";
import api from "../Data/api";
import moment from "moment";

function PackingReport() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const admin = JSON.parse(sessionStorage.getItem("admin"));
  const token = `Bearer ${admin.token}`;

  const [orders, setOrders] = useState(null);
  const [subsOrders, setSubsOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState(moment().format("YYYY-MM-DD")); // defaults to today
  const [viewMode, setViewMode] = useState("product"); // product | hourly | raw
  const [orderTypeFilter, setOrderTypeFilter] = useState("all"); // all | normal | subscription

  useEffect(() => {
    fetchAllData(filterDate || moment().format("YYYY-MM-DD"));
  }, [filterDate]);

  const fetchAllData = async (date) => {
    console.log("fetchAllData called with date:", date);
    setLoading(true);
    setSubsOrders([]);
    try {
      // Fetch all orders (for normal orders)
      const ordersUrl = `${api}/get_order`;
      const ordersRes = await GET(token, ordersUrl);
      if (ordersRes?.data) {
        setOrders(ordersRes.data);
      }

      // Fetch subscription orders for specific date (same as Upcoming Subs page)
      const subsUrl = `${api}/get_upcoming_delivery/sub_date/${date}`;
      console.log("Fetching subs from:", subsUrl);
      const subsRes = await GET(token, subsUrl);
      console.log("Subs response:", subsRes?.data?.length, "orders");
      if (subsRes?.data && Array.isArray(subsRes.data)) {
        setSubsOrders(subsRes.data);
      } else {
        setSubsOrders([]);
      }
    } catch (e) {
      console.error("Error fetching packing data:", e);
    }
    setLoading(false);
  };

  // Filter orders: show all orders that need packing for selected date
  const getActiveOrders = () => {
    let result = [];
    const selectedDate = filterDate || moment().format("YYYY-MM-DD");
    const dateMoment = moment(selectedDate);
    const dayCode = dateMoment.day(); // 0=Sun, 1=Mon...6=Sat

    // ─── SUBSCRIPTION ORDERS (filtered by delivery schedule) ───
    if (orderTypeFilter === "all" || orderTypeFilter === "subscription") {
      const filteredSubs = subsOrders.filter((o) => {
        // Monthly (type 3): deliver EVERY DAY — always show
        if (o.subscription_type === 3) return true;

        // Daily / One time (type 1): deliver EVERY DAY — always show
        if (o.subscription_type === 1) return true;

        // Weekly (type 2): only on selected weekdays
        if (o.subscription_type === 2) {
          if (o.selected_days_for_weekly) {
            try {
              let str = o.selected_days_for_weekly;
              if (!str || str === "null" || str === "[]") return true;
              if (typeof str === "string") {
                str = str.replace(/(['"])?([a-zA-Z0-9_]+)(['"])?:/g, '"$2":');
                const days = JSON.parse(str);
                return days.some((d) => Number(d.dayCode) === dayCode);
              }
              if (Array.isArray(str)) {
                return str.some((d) => Number(d.dayCode) === dayCode);
              }
            } catch (e) {
              return true;
            }
          }
          return true;
        }

        // Alternative days (type 4): every other day from start_date
        if (o.subscription_type === 4) {
          if (o.start_date) {
            const diffDays = dateMoment.diff(moment(o.start_date), "days");
            return diffDays >= 0 && diffDays % 2 === 0;
          }
          return true;
        }

        // Any other type: always show
        return true;
      });
      result = [...result, ...filteredSubs];
    }

    // ─── NORMAL ORDERS (only for the selected date) ───
    if ((orderTypeFilter === "all" || orderTypeFilter === "normal") && orders) {
      const normalOrders = orders.filter((o) => {
        if (o.subscription_type !== null) return false;
        const status = Number(o.status);
        if (status === 2) return false;
        const orderDate = moment(o.created_at).format("YYYY-MM-DD");
        return orderDate === selectedDate;
      });
      result = [...result, ...normalOrders];
    }

    return result;
  };

  const activeOrders = getActiveOrders();

  // Group by product
  const getProductSummary = () => {
    const map = {};
    activeOrders.forEach((o) => {
      const key = o.title || "Unknown";
      if (!map[key]) {
        map[key] = {
          title: key,
          qtyText: o.qty_text || "",
          totalQty: 0,
          totalOrders: 0,
          totalAmount: 0,
          hasBottle: 0,
          subscriptionOrders: 0,
          normalOrders: 0,
        };
      }
      map[key].totalQty += Number(o.qty) || 0;
      map[key].totalOrders += 1;
      map[key].totalAmount += Number(o.order_amount) || 0;
      if (o.has_bottle === 1) map[key].hasBottle += 1;
      if (o.subscription_type !== null) {
        map[key].subscriptionOrders += 1;
      } else {
        map[key].normalOrders += 1;
      }
    });
    return Object.values(map).sort((a, b) => b.totalQty - a.totalQty);
  };

  // Group by hour
  const getHourlySummary = () => {
    const map = {};
    activeOrders.forEach((o) => {
      const hour = moment.utc(o.created_at).utcOffset("+05:30").format("HH");
      if (!map[hour]) map[hour] = { hour, count: 0, amount: 0 };
      map[hour].count += 1;
      map[hour].amount += Number(o.order_amount) || 0;
    });
    return Object.values(map).sort((a, b) => a.hour.localeCompare(b.hour));
  };

  const productSummary = getProductSummary();
  const hourlySummary = getHourlySummary();
  const totalQty = activeOrders.reduce((s, o) => s + (Number(o.qty) || 0), 0);
  const totalAmount = activeOrders.reduce((s, o) => s + (Number(o.order_amount) || 0), 0);
  const totalBottles = activeOrders.filter((o) => o.has_bottle === 1).length;

  return (
    <Box>
      <Typography variant="h3" fontWeight={600} sx={{ mb: 1 }}>
        📦 Packing Report
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Consolidated view of all incoming orders to help you prepare and pack items efficiently.
      </Typography>

      {/* Filters */}
      <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
        <TextField
          type="date"
          size="small"
          color="secondary"
          label="Filter by Date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => setFilterDate("")}
        >
          Show All Dates
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          onClick={() => setFilterDate(moment().format("YYYY-MM-DD"))}
        >
          Today
        </Button>

        {/* Order Type Filter */}
        <Select
          size="small"
          color="secondary"
          value={orderTypeFilter}
          onChange={(e) => setOrderTypeFilter(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="all">All Orders</MenuItem>
          <MenuItem value="normal">Normal Only</MenuItem>
          <MenuItem value="subscription">Subscription Only</MenuItem>
        </Select>

        <Box sx={{ ml: "auto" }}>
          <Select
            size="small"
            color="secondary"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="product">By Product</MenuItem>
            <MenuItem value="hourly">By Hour</MenuItem>
            <MenuItem value="raw">All Orders</MenuItem>
          </Select>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
            <Box flex={1} minWidth="160px" p={2.5} borderRadius="12px" sx={{ background: "linear-gradient(135deg, #667eea, #764ba2)", color: "#fff" }}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Total Orders</Typography>
              <Typography variant="h3" fontWeight={800}>{activeOrders.length}</Typography>
            </Box>
            <Box flex={1} minWidth="160px" p={2.5} borderRadius="12px" sx={{ background: "linear-gradient(135deg, #43e97b, #38f9d7)", color: "#1a1a2e" }}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Total Quantity</Typography>
              <Typography variant="h3" fontWeight={800}>{totalQty}</Typography>
            </Box>
            <Box flex={1} minWidth="160px" p={2.5} borderRadius="12px" sx={{ background: "linear-gradient(135deg, #f093fb, #f5576c)", color: "#fff" }}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Total Amount</Typography>
              <Typography variant="h3" fontWeight={800}>₹{totalAmount.toFixed(0)}</Typography>
            </Box>
            <Box flex={1} minWidth="160px" p={2.5} borderRadius="12px" sx={{ background: "linear-gradient(135deg, #4facfe, #00f2fe)", color: "#1a1a2e" }}>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Bottle Pickups</Typography>
              <Typography variant="h3" fontWeight={800}>{totalBottles}</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Product View */}
          {viewMode === "product" && (
            <Box>
              {/* Subscription Orders Section - Milk Products */}
              {(() => {
                const subsOrders = activeOrders.filter(o => o.subscription_type !== null);
                const subsMap = {};
                subsOrders.forEach((o) => {
                  const key = `${o.title || "Unknown"}_${o.qty_text || ""}`;
                  if (!subsMap[key]) {
                    subsMap[key] = {
                      title: o.title || "Unknown",
                      packSize: o.qty_text || "",
                      unit: (o.qty_text || "").replace(/[0-9\s]/g, "").toUpperCase() || "ML",
                      packs: 0,
                      totalQty: 0,
                    };
                  }
                  subsMap[key].packs += Number(o.qty) || 1;
                  // Calculate total quantity in litres/kg
                  const numericSize = parseFloat((o.qty_text || "0").replace(/[^0-9.]/g, "")) || 0;
                  subsMap[key].totalQty += numericSize * (Number(o.qty) || 1);
                });
                const subsProducts = Object.values(subsMap).sort((a, b) => b.packs - a.packs);
                const totalPacks = subsProducts.reduce((s, p) => s + p.packs, 0);
                const totalQuantity = subsProducts.reduce((s, p) => s + p.totalQty, 0);
                // Determine unit for display
                const mainUnit = subsProducts.length > 0 ? subsProducts[0].unit : "ML";
                const displayUnit = mainUnit.includes("ML") || mainUnit.includes("LTR") ? "Ltr." : "Kg.";
                const displayTotal = mainUnit.includes("ML") || mainUnit.includes("LTR") ? (totalQuantity / 1000).toFixed(1) : (totalQuantity / 1000).toFixed(2);

                return (
                  <Box mb={4}>
                    <Typography variant="h5" fontWeight={700} mb={2} sx={{ color: "#2D7D46" }}>
                      🥛 Subscription Orders (Milk & Daily Products)
                    </Typography>
                    {subsProducts.length === 0 ? (
                      <Typography color="text.secondary" mb={2}>No subscription orders for this period.</Typography>
                    ) : (
                      <Box sx={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", border: "1px solid #d2d2d2" }}>
                          <thead>
                            <tr style={{ backgroundColor: "#2D7D46", color: "#fff" }}>
                              <th style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Sr. No.</th>
                              <th style={{ padding: "10px 14px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Product</th>
                              <th style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Pack Size</th>
                              <th style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Unit</th>
                              <th style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>No. of Packs</th>
                              <th style={{ padding: "10px 14px", textAlign: "center" }}>Total Quantity ({displayUnit})</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subsProducts.map((p, idx) => {
                              const numericSize = parseFloat(p.packSize.replace(/[^0-9.]/g, "")) || 0;
                              const itemTotal = p.unit.includes("ML") || p.unit.includes("LTR") ? (p.totalQty / 1000).toFixed(1) : (p.totalQty / 1000).toFixed(2);
                              return (
                                <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                                  <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>{idx + 1}</td>
                                  <td style={{ padding: "10px 14px", fontWeight: 600, borderRight: "1px solid #e2e8f0" }}>{p.title}</td>
                                  <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>{numericSize || p.packSize}</td>
                                  <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>{p.unit || "ML"}</td>
                                  <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>{p.packs}</td>
                                  <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700 }}>{itemTotal}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ backgroundColor: "#f0fdf4", fontWeight: 700 }}>
                              <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>Total</td>
                              <td style={{ padding: "10px 14px", borderRight: "1px solid #e2e8f0" }}>Subscription Products</td>
                              <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>—</td>
                              <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>—</td>
                              <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0", color: "#2D7D46", fontSize: "16px" }}>{totalPacks} Packs</td>
                              <td style={{ padding: "10px 14px", textAlign: "center", color: "#2D7D46", fontSize: "16px" }}>{displayTotal} {displayUnit}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Normal Orders Section - Dairy Products */}
              {(() => {
                const normalOrders = activeOrders.filter(o => o.subscription_type === null);
                const normalMap = {};
                normalOrders.forEach((o) => {
                  const key = `${o.title || "Unknown"}_${o.qty_text || ""}`;
                  if (!normalMap[key]) {
                    normalMap[key] = {
                      title: o.title || "Unknown",
                      packSize: o.qty_text || "",
                      unit: (o.qty_text || "").replace(/[0-9\s]/g, "").toUpperCase() || "GM",
                      packs: 0,
                      totalQty: 0,
                    };
                  }
                  normalMap[key].packs += Number(o.qty) || 1;
                  const numericSize = parseFloat((o.qty_text || "0").replace(/[^0-9.]/g, "")) || 0;
                  normalMap[key].totalQty += numericSize * (Number(o.qty) || 1);
                });
                const normalProducts = Object.values(normalMap).sort((a, b) => b.packs - a.packs);
                const totalPacks = normalProducts.reduce((s, p) => s + p.packs, 0);
                const totalQuantity = normalProducts.reduce((s, p) => s + p.totalQty, 0);
                const mainUnit = normalProducts.length > 0 ? normalProducts[0].unit : "GM";
                const displayUnit = mainUnit.includes("ML") || mainUnit.includes("LTR") ? "Ltr." : "Kg.";
                const displayTotal = mainUnit.includes("ML") || mainUnit.includes("LTR") ? (totalQuantity / 1000).toFixed(1) : (totalQuantity / 1000).toFixed(2);

                return (
                  <Box mb={4}>
                    <Typography variant="h5" fontWeight={700} mb={2} sx={{ color: "#764ba2" }}>
                      🧀 Normal Orders (Dairy & One-time Products)
                    </Typography>
                    {normalProducts.length === 0 ? (
                      <Typography color="text.secondary" mb={2}>No normal orders for this period.</Typography>
                    ) : (
                      <Box sx={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px", border: "1px solid #d2d2d2" }}>
                          <thead>
                            <tr style={{ backgroundColor: "#764ba2", color: "#fff" }}>
                              <th style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Sr. No.</th>
                              <th style={{ padding: "10px 14px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Product</th>
                              <th style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Pack Size</th>
                              <th style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>No. of Packs</th>
                              <th style={{ padding: "10px 14px", textAlign: "center" }}>Total Quantity ({displayUnit})</th>
                            </tr>
                          </thead>
                          <tbody>
                            {normalProducts.map((p, idx) => {
                              const itemTotal = p.unit.includes("ML") || p.unit.includes("LTR") ? (p.totalQty / 1000).toFixed(1) : (p.totalQty / 1000).toFixed(2);
                              return (
                                <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                                  <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>{idx + 1}</td>
                                  <td style={{ padding: "10px 14px", fontWeight: 600, borderRight: "1px solid #e2e8f0" }}>{p.title}</td>
                                  <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>{p.packSize}</td>
                                  <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>{p.packs}</td>
                                  <td style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700 }}>{itemTotal}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr style={{ backgroundColor: "#f5f0ff", fontWeight: 700 }}>
                              <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>Total</td>
                              <td style={{ padding: "10px 14px", borderRight: "1px solid #e2e8f0" }}>Dairy Products</td>
                              <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>—</td>
                              <td style={{ padding: "10px 14px", textAlign: "center", borderRight: "1px solid #e2e8f0", color: "#764ba2", fontSize: "16px" }}>{totalPacks} Packs</td>
                              <td style={{ padding: "10px 14px", textAlign: "center", color: "#764ba2", fontSize: "16px" }}>{displayTotal} {displayUnit}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </Box>
                    )}
                  </Box>
                );
              })()}

              {/* Customer-wise Daily Details */}
              <Box mt={4}>
                <Typography variant="h5" fontWeight={700} mb={2} sx={{ color: "#1a1a2e" }}>
                  📋 Customer-wise Daily Packing Details
                </Typography>
                {activeOrders.length === 0 ? (
                  <Typography color="text.secondary">No orders for this date.</Typography>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", border: "1px solid #d2d2d2" }}>
                      <thead>
                        <tr style={{ backgroundColor: "#1a1a2e", color: "#fff" }}>
                          <th style={{ padding: "10px 12px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Sr.</th>
                          <th style={{ padding: "10px 12px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Customer</th>
                          <th style={{ padding: "10px 12px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Phone</th>
                          <th style={{ padding: "10px 12px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Product</th>
                          <th style={{ padding: "10px 12px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Pack Size</th>
                          <th style={{ padding: "10px 12px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Qty</th>
                          <th style={{ padding: "10px 12px", textAlign: "center", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Type</th>
                          <th style={{ padding: "10px 12px", textAlign: "left", borderRight: "1px solid rgba(255,255,255,0.2)" }}>Address</th>
                          <th style={{ padding: "10px 12px", textAlign: "center" }}>Bottle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeOrders.map((o, idx) => (
                          <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0", backgroundColor: idx % 2 === 0 ? "#f9fafb" : "#fff" }}>
                            <td style={{ padding: "8px 12px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>{idx + 1}</td>
                            <td style={{ padding: "8px 12px", fontWeight: 600, borderRight: "1px solid #e2e8f0" }}>{o.name || "—"}</td>
                            <td style={{ padding: "8px 12px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>{o.s_phone || "—"}</td>
                            <td style={{ padding: "8px 12px", borderRight: "1px solid #e2e8f0" }}>{o.title || "—"}</td>
                            <td style={{ padding: "8px 12px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>{o.qty_text || "—"}</td>
                            <td style={{ padding: "8px 12px", textAlign: "center", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>{o.qty || 1}</td>
                            <td style={{ padding: "8px 12px", textAlign: "center", borderRight: "1px solid #e2e8f0" }}>
                              <span style={{
                                padding: "3px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 600,
                                backgroundColor: o.subscription_type !== null ? "#e0f2fe" : "#fef3c7",
                                color: o.subscription_type !== null ? "#0369a1" : "#d97706"
                              }}>
                                {o.subscription_type === 2 ? "Weekly" : o.subscription_type === 3 ? "Monthly" : o.subscription_type === 4 ? "Alt Days" : o.subscription_type !== null ? "Subs" : "Normal"}
                              </span>
                            </td>
                            <td style={{ padding: "8px 12px", fontSize: "12px", color: "#4a5568", borderRight: "1px solid #e2e8f0" }}>
                              {`${o.flat_no || ""} ${o.apartment_name || ""} ${o.area || ""} ${o.city || ""} ${o.pincode || ""}`.trim() || "—"}
                            </td>
                            <td style={{ padding: "8px 12px", textAlign: "center" }}>
                              {o.has_bottle === 1 ? "🍶" : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Hourly View */}
          {viewMode === "hourly" && (
            <Box>
              <Typography variant="h5" fontWeight={700} mb={2}>
                ⏰ Hourly Order Distribution
              </Typography>
              {hourlySummary.length === 0 ? (
                <Typography color="text.secondary">No orders found for selected date.</Typography>
              ) : (
                <Box display="flex" gap={1.5} flexWrap="wrap">
                  {hourlySummary.map((h) => (
                    <Box
                      key={h.hour}
                      p={2}
                      borderRadius="12px"
                      textAlign="center"
                      minWidth="90px"
                      sx={{ backgroundColor: colors.primary[400], border: "1px solid #e2e8f0" }}
                    >
                      <Typography variant="body2" fontWeight={600} color="text.secondary">{h.hour}:00</Typography>
                      <Typography variant="h4" fontWeight={800} color="#2D7D46">{h.count}</Typography>
                      <Typography variant="caption" color="text.secondary">₹{h.amount.toFixed(0)}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Raw Orders View */}
          {viewMode === "raw" && (
            <Box>
              <Typography variant="h5" fontWeight={700} mb={2}>
                📋 All Active Orders ({activeOrders.length})
              </Typography>
              {activeOrders.length === 0 ? (
                <Typography color="text.secondary">No orders found for selected date.</Typography>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ backgroundColor: colors.navbarBG[400], color: "#f5f5f5" }}>
                        <th style={{ padding: "10px 12px", textAlign: "left" }}>ID</th>
                        <th style={{ padding: "10px 12px", textAlign: "left" }}>Product</th>
                        <th style={{ padding: "10px 12px", textAlign: "center" }}>Qty</th>
                        <th style={{ padding: "10px 12px", textAlign: "center" }}>Amount</th>
                        <th style={{ padding: "10px 12px", textAlign: "left" }}>Customer</th>
                        <th style={{ padding: "10px 12px", textAlign: "center" }}>Type</th>
                        <th style={{ padding: "10px 12px", textAlign: "center" }}>Status</th>
                        <th style={{ padding: "10px 12px", textAlign: "center" }}>Order Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeOrders.map((o, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "10px 12px" }}>#{o.id}</td>
                          <td style={{ padding: "10px 12px", fontWeight: 600 }}>{o.title} <span style={{ color: "#718096", fontWeight: 400 }}>({o.qty_text})</span></td>
                          <td style={{ padding: "10px 12px", textAlign: "center" }}>{o.qty}</td>
                          <td style={{ padding: "10px 12px", textAlign: "center" }}>₹{o.order_amount}</td>
                          <td style={{ padding: "10px 12px" }}>{o.name} <br/><span style={{ fontSize: "11px", color: "#718096" }}>{o.s_phone}</span></td>
                          <td style={{ padding: "10px 12px", textAlign: "center" }}>
                            {o.subscription_type !== null ? (
                              <Chip label="Subs" size="small" color="primary" variant="outlined" />
                            ) : (
                              <Chip label="Normal" size="small" color="secondary" variant="outlined" />
                            )}
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "center" }}>
                            <span style={{
                              padding: "3px 10px", borderRadius: "12px", fontSize: "11px", fontWeight: 600,
                              backgroundColor: Number(o.status) === 1 ? "#dcfce7" : "#fef3c7",
                              color: Number(o.status) === 1 ? "#16a34a" : "#d97706"
                            }}>
                              {Number(o.status) === 1 ? "Confirmed" : "Pending"}
                            </span>
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "center", fontSize: "12px" }}>
                            {moment.utc(o.created_at).utcOffset("+05:30").format("DD-MM HH:mm")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default PackingReport;
